import {
  AppConfig,
  AppSource,
  AppVersion,
  EnabledPlatformFeatures,
  PlatformFeature,
  arePlatformFeaturesEnabled,
  hasPlatformFeature,
  PLATFORM_FEATURES,
  resolveEffectiveAppSource,
} from '@application/config';
import { EPaguelaSituation } from '@financial/data-access';
import { EShortcutIdentifier } from '@shared/components/shortcuts/data-access';
import {
  SystemModules,
  PermissionNumber,
} from '@domains/person/user/self/data';
import {
  EAuditOrigin,
  TAutoRenewalCanceledDetails,
  TCustomerAutoInactivatedDetails,
  TCustomerStatusChangedDetails,
  TInactivationBlockedDetails,
  TInactivationChoiceDetails,
  TInactivationRulesSavedDetails,
  TRenewalsDeletedDetails,
  TSentToCrmDetails,
  TSubscriptionBreakDetails,
  TSubscriptionDeletedDetails,
  TSubscriptionEndedDetails,
  TSubscriptionFinishedDetails,
  TSubscriptionMovedDetails,
  TSubscriptionUnpausedDetails,
} from '@settings/data-access';

export type TResponsible = {
  PersonId: string;
  Kinship: EKinshipType | null;
  FiscalIdentity: string | null;
  PhoneNumber: string | null;
  FullPhoneNumber: string | null;
  CountryCode: number | null;
  FirstName: string | null;
  LastName: string | null;
  TenantId: string | null;
  Email: string | null;
  Operation: number | null;
  ValidationResult: string | null;
  IsValid: boolean;
};

export type TPersonTie = {
  PersonId: string;
  TenantId: string | null;
  TieId: string;
  IsActive: boolean;
  IsResponsible: boolean;
  Kinship: EKinshipType | null;
  TieFirstName?: string | null;
  TieLastName?: string | null;
  TieFullName?: string | null;
  TieDisplayName?: string | null;
  TieFullDisplayName?: string | null;
  FirstName?: string | null;
  LastName?: string | null;
  FullName?: string | null;
  DisplayName?: string | null;
  FullDisplayName?: string | null;
};

export type TPersonLookupSummary = {
  PersonId: string;
  FullDisplayName: string | null;
  FullName: string | null;
  Email: string | null;
  Phones: string | null;
  IsActive: boolean | null;
};

export enum EPersonType {
  Person = 1,
  Company = 2,
}

export enum EGenderType {
  Others = 0,
  Male = 1,
  Female = 2,
}

export enum ERegistrationType {
  Contact,
  Customer,
  Helpdesk,
  Supplier,
  User,
  Visitor,
}

export const registrationShortcutIdentifier = {
  [ERegistrationType.Customer]: EShortcutIdentifier.NewClient,
  [ERegistrationType.Visitor]: EShortcutIdentifier.NewVisitor,
};

export const registrationTitles = {
  [ERegistrationType.Contact]: {
    title: 'PROFILE.EDIT.REGISTRATION.CONTACT.TITLE',
    subtitle: 'PROFILE.EDIT.REGISTRATION.CONTACT.SUBTITLE',
  },
  [ERegistrationType.Customer]: {
    title: 'PROFILE.EDIT.REGISTRATION.CUSTOMER.TITLE',
    subtitle: 'PROFILE.EDIT.REGISTRATION.CUSTOMER.SUBTITLE',
  },
  [ERegistrationType.Visitor]: {
    title: 'PROFILE.EDIT.REGISTRATION.VISITOR.TITLE',
    subtitle: 'PROFILE.EDIT.REGISTRATION.VISITOR.SUBTITLE',
  },
  [ERegistrationType.User]: {
    title: 'PROFILE.EDIT.REGISTRATION.USER.TITLE',
    subtitle: 'PROFILE.EDIT.REGISTRATION.USER.SUBTITLE',
  },
  [ERegistrationType.Supplier]: {
    title: 'PROFILE.EDIT.REGISTRATION.SUPPLIER.TITLE',
    subtitle: 'PROFILE.EDIT.REGISTRATION.SUPPLIER.SUBTITLE',
  },
};

export const registrationTitlesSwitch = {
  [ERegistrationType.Contact]: {
    title: 'PROFILE.EDIT.ACTIVATE_CONTACT',
    subtitle: 'PROFILE.EDIT.ACTIVATE_CONTACT_DESCRIPTION',
  },
  [ERegistrationType.Customer]: {
    title: 'PROFILE.EDIT.ACTIVATE_CUSTOMER',
    subtitle: 'PROFILE.EDIT.ACTIVATE_CUSTOMER_DESCRIPTION',
  },
  [ERegistrationType.Visitor]: {
    title: 'PROFILE.EDIT.ACTIVATE_VISITOR',
    subtitle: 'PROFILE.EDIT.ACTIVATE_VISITOR_DESCRIPTION',
  },
  [ERegistrationType.User]: {
    title: 'PROFILE.EDIT.ACTIVATE_USER',
    subtitle: 'PROFILE.EDIT.ACTIVATE_USER_DESCRIPTION',
  },
  [ERegistrationType.Supplier]: {
    title: 'PROFILE.EDIT.ACTIVATE_SUPPLIER',
    subtitle: 'PROFILE.EDIT.ACTIVATE_SUPPLIER_DESCRIPTION',
  },
};

export enum EPhoneType {
  Home = 1,
  Mobile = 2,
  Commerce = 3,
  Other = 4,
  MobileNotification = 5,
}

export enum EAddressType {
  Others = 0,
  Home = 1,
  Business = 2,
}

export type TProfile = {
  PersonId: string;
  IsLegacy: boolean | null;
  AppVersion: AppVersion | null;
  TenantId: string | null;
  AbstractedTenantId: string | null;
  FriendlyCode: string | null;
  CallCode: string | null;
  IsOwner: boolean | null;
  IsActive: boolean | null;
  FirstName: string | null;
  LastName: string | null;
  FullName: string | null;
  DisplayName: string | null;
  FriendlyName: string | null;
  LegalBusinessName: string | null;
  TradeName: string | null;
  Email: string | null;
  Registration?: string | null;
  PersonType: EPersonType | null;
  GenderType: EGenderType | null;
  IdentityDocument: string | null;
  CompanyIdentityDocument: string | null;
  IdentityDocumentComplement: string | null;
  FiscalIdentity: string | null;
  CompanyFiscalIdentity: string | null;
  Birthdate: string | null;
  Age: number | null;
  Tags: string | null;
  Origin?: string | null;
  RegistrationType: ERegistrationType | null;
  AbstractedRegistrationType: ERegistrationType | null;
  IsResponsible?: boolean | null;
  ActivitiesIds?: string[] | null;
  GroupId: string | null;
  ModuleId: string | null;
  AddedOn: string | null;
  AddedBy: string | null;
  UpdatedOn: string | null;
  UpdatedBy: string | null;
  DeletedOn: string | null;
  DeletedBy: string | null;
  Phones: TPhone[];
  Addresses: TAddress[];
  UserConsolidated: TUserConsolidated;
  OperationOrigemIsLegacy: boolean | null;
  CallerData: TCallerData;
  Operation: string | null;
  Preference?: TPreference;
  Responsible?: TResponsible;
  Ties?: TPersonTie[];
  HistoryConsolidated?: THistoryConsolidated;
  EventConsolidated?: TEventConsolidated;
  Integration?: TIntegration;
  Tax: TTax;
  Access?: TAccess;
  ValidationResult: string | null;
  IsValid: boolean;
  FullDisplayName: string;
};

export type TSimpleLoginCode = {
  AddedBy?: string;
  AddedOn?: string;
  ExpiresIn?: string;
  LoginCodeId?: string;
  PersonId?: string;
  Sid?: string;
  TenantId?: string;
  Type?: string;
};

export type TIntegration = {
  PagueLaId?: string;
};

export type TTax = {
  PersonId: string;
  TenantId: string;
  Type?: ETaxRegimeType;
  EspecialType?: ESpecialTaxRegimeType;
  StartOn?: string;
  EndOn?: string;
};

export type TEventConsolidated = {
  MaxAccessAllowedOn: string | null;
  LicenseExpirationOn: string | null;
};

export type THistoryConsolidated = {
  AccessBlockedOn: string | null;
  LastNameChangedOn: string | null;
  LastLoggedOn: string | null;
  FirstActivedOn?: string | null;
  LastActivedOn?: string | null;
  PersonId?: string | null;
  FirstSubscriptionReceivedOn?: string | null;
  ActivationUserId?: string | null;
  LastInactivatedOn?: string | null;
  InactivationUserId?: string | null;
  LastPersonTypeChangedOn?: string | null;
  LastAvatarChangedOn?: string | null;
  LastDesktopSincronizedOn?: string | null;
  LastReceivedOn?: string | null;
  LastSubscriptionReceivedOn?: string | null;
  LastInAccessOn?: string | null;
  LastOutAccessOn?: string | null;
  ClientConvertedOn?: string | null;
};

export type TPreference = {
  ConsultantPersonId?: string;
  PersonId?: string;
  TenantId?: string;
  PreferredDueDay?: string;
};

export type TPhone = {
  PhoneId: string;
  PersonId: string | null;
  TenantId: string | null;
  Type: EPhoneType | null;
  Number: string | null;
  CountryCode: number | null;
  Confirmed: boolean | null;
  Comment: string | null;
  FullNumber: string | null;
};

export type TAddress = {
  AddressId: string;
  PersonId: string | null;
  TenantId: string | null;
  Type: EAddressType | null;
  CountryCode: string | null;
  StreetType: string | null;
  Street: string | null;
  Number: string | null;
  Complement: string | null;
  RegionName: string | null;
  City: string | null;
  District: string | null;
  ZipCode: string | null;
  CityCode: string | null;
  IsFiscal: boolean | null;
};

export type TUserConsolidated = {
  PersonId: string;
  RoleId: string | null;
  RoleDescription: string | null;
  PasswordCreatedOn: string | null;
  PasswordResetedOn: string | null;
  LastLoginOn: string | null;
  LastLoginApp: string | null;
  Operation: string | null;
  ValidationResult: string | null;
  IsValid: boolean;
};

export type TCallerData = {
  MemberName: string | null;
  SourceFilePath: string | null;
  SourceLineNumber: number | null;
};

export type TComment = {
  CommentId?: string;
  Title: string;
  Read?: boolean;
  Content: string;
  AddedBy?: string;
  AddedOn?: string;
  UpdatedOn?: string;
  UserName?: string;
  TargetPersonId?: string;
  SourcePersonId?: string;
};

export type TCommentToggleRead = {
  CommentId: string;
  Read: boolean;
};

export type TActivity = {
  ActivityId?: string;
  Description: string;
  Icon?: string;
};

export type TAccess = {
  AccessPin?: string | null;
  AccessMessage?: string | null;
};

export const predefinedActivities: TActivity[] = [
  {
    Description: 'Beach Tennis',
    Icon: 'racquet',
  },
  {
    Description: 'Boxe',
    Icon: 'boxing-glove',
  },
  {
    Description: 'Taekwondo',
    Icon: 'boxing-glove',
  },
  {
    Description: 'Muay Thai',
    Icon: 'boxing-glove',
  },
  {
    Description: 'Basquete',
    Icon: 'basketball',
  },
  {
    Description: 'Capoeira',
    Icon: 'scarf',
  },
  {
    Description: 'Cross Fit',
    Icon: 'dumbbell-ray',
  },
  {
    Description: 'Cross Training',
    Icon: 'dumbbell-ray',
  },
  {
    Description: 'Musculação',
    Icon: 'dumbbell-horizontal',
  },
  {
    Description: 'Corrida',
    Icon: 'running',
  },
  {
    Description: 'Funcional',
    Icon: 'running',
  },
  {
    Description: 'FitDance',
    Icon: 'swing',
  },
  {
    Description: 'Futebol',
    Icon: 'court-sport',
  },
  {
    Description: 'Futevôlei',
    Icon: 'football',
  },
  {
    Description: 'Hidroginástica',
    Icon: 'swimmer',
  },
  {
    Description: 'Natação',
    Icon: 'swimmer',
  },
  {
    Description: 'Jiu Jitsu',
    Icon: 'uniform-martial-arts',
  },
  {
    Description: 'Judô',
    Icon: 'uniform-martial-arts',
  },
  {
    Description: 'Karatê',
    Icon: 'uniform-martial-arts',
  },
  {
    Description: 'Pilates',
    Icon: 'person-pilates',
  },
  {
    Description: 'Ritmos',
    Icon: 'ballet-dance',
  },
  {
    Description: 'Spinning',
    Icon: 'biking',
  },
  {
    Description: 'Tênis',
    Icon: 'tennis',
  },
  {
    Description: 'Vôlei',
    Icon: 'volleyball',
  },
  {
    Description: 'Vôlei de Areia',
    Icon: 'volleyball',
  },
  {
    Description: 'Yoga',
    Icon: 'meditation',
  },
  {
    Description: 'Zumba',
    Icon: 'transporter',
  },
];

export const mockedActivities: TActivity[] = [
  {
    Description: 'Ritmos',
    Icon: 'ballet-dance',
  },
  {
    Description: 'Spinning',
    Icon: 'biking',
  },
  {
    Description: 'Taekwondo',
    Icon: 'boxing-glove',
  },
];

export enum EProfileMenuOption {
  EDIT_PROFILE = '1',
  ADD_SUBSCRIPTION = '2',
  ADD_SERVICE = '3',
  ADD_SALE = '4',
  NEW_TREINO = '5',
  NEW_PHYSICAL_ASSESSMENT = '6',
  NEW_GRADUATION = '7',
  SEND_CRM = '8',
  ADD_CONTRACT = '9',
  ADD_DOCUMENT = '10',
  SEND_EMAIL = '11',
  SEND_INVITE_SMS = 'send_invite_sms',
  REGISTER_BIOMETRICS = '12',
  ADD_COMMENT = '13',
  CONVERT_TO_CUSTOMER = 'convert_to_customer',
  CONVERT_TO_SUPPLIER = 'convert_to_supplier',
  CONVERT_TO_USER = 'convert_to_user',
  NEW_RETURN = 'new_return',
  MANAGE_NEGATIVATION = 'manage_negativation',
  ADD_ENROLLMENT = 'add_enrollment',
}

export const isSchoolStudent = ({
  registrationType,
  enabledPlatformFeatures,
}: {
  registrationType: ERegistrationType | null | undefined;
  enabledPlatformFeatures?: EnabledPlatformFeatures | null;
}): boolean =>
  registrationType === ERegistrationType.Customer &&
  hasPlatformFeature(enabledPlatformFeatures, PLATFORM_FEATURES.school);

export type TProfileMenuOptionItem = {
  Id: string;
  Description: string;
  Group: string;
  RightIcon?: string;
  onlyTypes?: ERegistrationType[];
  isAdmin?: boolean;
  isPaguelaAdmin?: boolean;
  requiredModules?: string[];
  requiredAnyModules?: string[];
  requiredPlatformFeatures?: PlatformFeature[];
};

export const isEdizResponsibleTarget = ({
  isAdminScope,
  appSource,
  isResponsible,
}: {
  isAdminScope: boolean;
  appSource?: AppSource;
  isResponsible?: boolean | null;
}): boolean => !!isResponsible && !isAdminScope && appSource === AppSource.Ediz;

export const skipsUserProfileEmailFlow = ({
  registrationType,
  isAdminScope,
  isToletusAdmin,
  appSource,
  isResponsible,
}: {
  registrationType: ERegistrationType | null | undefined;
  isAdminScope: boolean;
  isToletusAdmin?: boolean;
  appSource?: AppSource;
  isResponsible?: boolean | null;
}): boolean => {
  const isCustomerOrVisitor =
    registrationType === ERegistrationType.Customer ||
    registrationType === ERegistrationType.Visitor;
  if (!isCustomerOrVisitor) {
    return false;
  }

  if (isEdizResponsibleTarget({ isAdminScope, appSource, isResponsible })) {
    return false;
  }

  const isEdizOrToletusApp =
    appSource === AppSource.Ediz || appSource === AppSource.Toletus;

  return (!isAdminScope && isEdizOrToletusApp) || !!isToletusAdmin;
};

export const menuOptions = ({
  enabledPlatformFeatures,
  isAdmin,
  isPaguelaAdmin,
  isToletusAdmin,
  registrationType,
  appSource,
  isResponsible,
}: {
  enabledPlatformFeatures?: EnabledPlatformFeatures | null;
  isAdmin?: boolean;
  isPaguelaAdmin?: boolean;
  isToletusAdmin?: boolean;
  registrationType: ERegistrationType;
  appSource?: AppSource;
  isResponsible?: boolean | null;
}) => {
  const mockMenuOptions: TProfileMenuOptionItem[] = [
    {
      Id: EProfileMenuOption.CONVERT_TO_CUSTOMER,
      Description: 'PROFILE.HEADER_ACTIONS.CONVERT_TO_CUSTOMER',
      Group: 'first',
      onlyTypes: [ERegistrationType.Visitor],
    },
    {
      Id: EProfileMenuOption.CONVERT_TO_SUPPLIER,
      Description: 'PROFILE.HEADER_ACTIONS.CONVERT_TO_SUPPLIER',
      Group: 'first',
      onlyTypes: [ERegistrationType.Contact],
    },
    {
      Id: EProfileMenuOption.EDIT_PROFILE,
      Description: 'PROFILE.HEADER_ACTIONS.EDIT_PROFILE',
      Group: 'first',
    },
    {
      Id: EProfileMenuOption.ADD_SUBSCRIPTION,
      Description: 'PROFILE.HEADER_ACTIONS.ADD_SUBSCRIPTION',
      Group: 'second',
      onlyTypes: isAdmin
        ? [ERegistrationType.Customer, ERegistrationType.Visitor]
        : [ERegistrationType.Customer],
      requiredModules: [SystemModules.Assinaturas],
    },
    {
      Id: EProfileMenuOption.ADD_SERVICE,
      Description: 'PROFILE.HEADER_ACTIONS.ADD_SERVICE',
      Group: 'second',
      onlyTypes: [
        ERegistrationType.Customer,
        ERegistrationType.Visitor,
        ERegistrationType.User,
      ],
      requiredPlatformFeatures: [PLATFORM_FEATURES.workServices],
      requiredModules: [SystemModules.Servicos],
    },
    {
      Id: EProfileMenuOption.ADD_SALE,
      Description: 'PROFILE.HEADER_ACTIONS.ADD_SALE',
      Group: 'second',
      onlyTypes: [
        ERegistrationType.Customer,
        ERegistrationType.Visitor,
        ERegistrationType.User,
      ],
      requiredPlatformFeatures: [PLATFORM_FEATURES.goods],
      requiredModules: [SystemModules.Mercadorias],
    },
    {
      Id: EProfileMenuOption.NEW_TREINO,
      Description: 'PROFILE.HEADER_ACTIONS.NEW_WORKOUT_SHEET',
      Group: 'third',
      onlyTypes: [
        ERegistrationType.Customer,
        ERegistrationType.Visitor,
        ERegistrationType.User,
      ],
      requiredModules: [SystemModules.TreiNo],
      requiredPlatformFeatures: [PLATFORM_FEATURES.treino],
    },
    {
      Id: EProfileMenuOption.NEW_PHYSICAL_ASSESSMENT,
      Description: 'PROFILE.HEADER_ACTIONS.NEW_PHYSICAL_ASSESSMENT',
      Group: 'third',
      onlyTypes: [
        ERegistrationType.Customer,
        ERegistrationType.Visitor,
        ERegistrationType.User,
      ],
      requiredModules: [SystemModules.TreiNo],
      requiredPlatformFeatures: [PLATFORM_FEATURES.treino],
    },
    {
      Id: EProfileMenuOption.NEW_GRADUATION,
      Description: 'PROFILE.HEADER_ACTIONS.NEW_GRADUATION',
      Group: 'third',
      onlyTypes: [ERegistrationType.Customer],
      requiredModules: [SystemModules.TreiNo],
      requiredPlatformFeatures: [PLATFORM_FEATURES.treino],
    },
    {
      Id: EProfileMenuOption.SEND_CRM,
      Description: 'PROFILE.HEADER_ACTIONS.SEND_CRM',
      Group: 'fourth',
      onlyTypes: [ERegistrationType.Customer, ERegistrationType.Visitor],
      requiredAnyModules: [SystemModules.CRM, SystemModules.Whatsapp],
    },
    {
      Id: EProfileMenuOption.ADD_CONTRACT,
      Description: 'PROFILE.HEADER_ACTIONS.ADD_CONTRACT',
      Group: 'fourth',
      onlyTypes: [ERegistrationType.Customer],
      requiredAnyModules: [SystemModules.ActuarWeb, SystemModules.Contratos],
    },
    {
      Id: EProfileMenuOption.ADD_DOCUMENT,
      Description: 'PROFILE.HEADER_ACTIONS.ADD_DOCUMENT',
      Group: 'fourth',
      onlyTypes: [ERegistrationType.Customer],
    },
    {
      Id: EProfileMenuOption.SEND_EMAIL,
      Description: 'PROFILE.HEADER_ACTIONS.SEND_EMAIL',
      Group: 'password-definition',
      onlyTypes: [ERegistrationType.Customer, ERegistrationType.User],
    },
    {
      Id: EProfileMenuOption.SEND_INVITE_SMS,
      Description: 'PROFILE.HEADER_ACTIONS.SEND_INVITE_SMS',
      Group: 'password-definition',
      requiredPlatformFeatures: [PLATFORM_FEATURES.school],
    },
    {
      Id: EProfileMenuOption.REGISTER_BIOMETRICS,
      Description: 'PROFILE.HEADER_ACTIONS.REGISTER_BIOMETRICS',
      Group: 'fifth',
      onlyTypes: [
        ERegistrationType.Customer,
        ERegistrationType.Visitor,
        ERegistrationType.User,
      ],
    },
    {
      Id: EProfileMenuOption.ADD_COMMENT,
      Description: 'PROFILE.HEADER_ACTIONS.ADD_COMMENT',
      Group: 'fourth',
    },
    {
      Id: EProfileMenuOption.MANAGE_NEGATIVATION,
      Description: 'PROFILE.HEADER_ACTIONS.MANAGE_NEGATIVATION',
      Group: 'fourth',
      onlyTypes: [ERegistrationType.Customer],
      isAdmin: true,
    },
    {
      Id: EProfileMenuOption.NEW_RETURN,
      Description: 'PROFILE.HEADER_ACTIONS.SEND_RETURN',
      Group: 'fourth',
      onlyTypes: [ERegistrationType.Customer],
      isPaguelaAdmin: true,
    },
    ...(isSchoolStudent({ registrationType, enabledPlatformFeatures })
      ? [
          {
            Id: EProfileMenuOption.ADD_ENROLLMENT,
            Description: 'PROFILE.HEADER_ACTIONS.ADD_ENROLLMENT',
            Group: 'second',
            onlyTypes: [ERegistrationType.Customer],
          },
        ]
      : []),
  ];

  return mockMenuOptions.filter((tab) => {
    const canShowByTypes =
      (tab.onlyTypes && tab.onlyTypes.includes(registrationType)) ||
      !tab.onlyTypes;
    const canShowByAdmin = (tab.isAdmin && isAdmin) || !tab.isAdmin;
    const canShowByPaguelaAdmin =
      (tab.isPaguelaAdmin && isPaguelaAdmin) || !tab.isPaguelaAdmin;

    const isSendEmail = tab.Id === EProfileMenuOption.SEND_EMAIL;
    const hideSendEmail = skipsUserProfileEmailFlow({
      registrationType,
      isAdminScope: !!isAdmin,
      isToletusAdmin,
      appSource,
      isResponsible,
    });

    if (
      isSendEmail &&
      isEdizResponsibleTarget({
        isAdminScope: !!isAdmin,
        appSource,
        isResponsible,
      })
    ) {
      return true;
    }

    return (
      canShowByTypes &&
      canShowByAdmin &&
      canShowByPaguelaAdmin &&
      !(isSendEmail && hideSendEmail)
    );
  });
};

export const filterVisibleProfileMenuOptions = (
  options: TProfileMenuOptionItem[],
  enabledPlatformFeatures: EnabledPlatformFeatures | null | undefined,
  isModuleAllowed: (moduleId: string) => boolean,
  isAnyModuleAllowed: (moduleIds: string[]) => boolean
): TProfileMenuOptionItem[] =>
  options.filter((option) => {
    const hasRequiredPlatformFeatures = arePlatformFeaturesEnabled(
      enabledPlatformFeatures,
      option.requiredPlatformFeatures
    );
    const hasRequiredModules =
      option.requiredModules?.every((moduleId) => isModuleAllowed(moduleId)) ??
      true;

    const hasRequiredAnyModules =
      !option.requiredAnyModules ||
      option.requiredAnyModules.length === 0 ||
      isAnyModuleAllowed(option.requiredAnyModules);

    return (
      hasRequiredPlatformFeatures && hasRequiredModules && hasRequiredAnyModules
    );
  });

export const resolveProfileHeaderActionAppSource = (
  appConfig: AppConfig,
  isPaguelaAdmin: boolean
): AppSource =>
  isPaguelaAdmin ? AppSource.PagueLa : resolveEffectiveAppSource(appConfig);

export type TProfileTabItem = {
  id: string;
  description: string;
  icon?: string;
  hideDescription?: boolean;
  route: string;
  onlyTypes?: ERegistrationType[];
  isAdmin?: boolean;
  isPaguelaAdmin?: boolean;
  hasPaguela?: boolean;
  isMobile?: boolean;
  requiredModules?: string[];
  requiredAnyModules?: string[];
  requiredPlatformFeatures?: PlatformFeature[];
  requiredPermissions?: PermissionNumber[];
};

export const tabs = ({
  isAdmin,
  isPaguelaAdmin,
  hasPaguela,
  registrationType,
  isMobile,
}: {
  isAdmin?: boolean;
  isPaguelaAdmin?: boolean;
  hasPaguela?: boolean;
  registrationType: ERegistrationType;
  isMobile?: boolean;
}) => {
  const mockTabs: TProfileTabItem[] = [
    {
      id: 'profile-data',
      description: 'PROFILE.TABS.PROFILE_DATA',
      icon: 'user',
      hideDescription: true,
      route: 'profile',
      isMobile: true,
    },
    {
      id: '1',
      description: 'PROFILE.TABS.INSIGHTS',
      icon: 'street-view',
      hideDescription: true,
      route: 'insights',
      onlyTypes: [
        ERegistrationType.Customer,
        ERegistrationType.Visitor,
        ERegistrationType.User,
      ],
      requiredAnyModules: [
        SystemModules.ActuarWeb,
        SystemModules.TreiNo,
        SystemModules.Acesso,
        SystemModules.Assinaturas,
        SystemModules.Mercadorias,
        SystemModules.Servicos,
        SystemModules.CRM,
        SystemModules.Whatsapp,
        SystemModules.Contratos,
      ],
    },
    {
      id: '2',
      description: 'PROFILE.TABS.TIMELINE',
      icon: 'branching',
      hideDescription: true,
      route: 'timeline',
    },
    {
      id: 'ties',
      description: 'PROFILE.TABS.TIES',
      icon: 'users',
      route: 'ties',
      requiredPlatformFeatures: [PLATFORM_FEATURES.school],
    },
    {
      id: 'enrollments',
      description: 'PROFILE.TABS.ENROLLMENTS',
      route: 'enrollments',
      onlyTypes: [ERegistrationType.Customer],
      requiredPlatformFeatures: [PLATFORM_FEATURES.school],
    },
    {
      id: 'attendance',
      description: 'PROFILE.TABS.ATTENDANCE',
      route: 'attendance',
      onlyTypes: [ERegistrationType.Customer],
      requiredPlatformFeatures: [PLATFORM_FEATURES.school],
    },
    {
      id: 'fiscal-data',
      description: 'PROFILE.FISCAL_DATA.TITLE',
      route: 'fiscal-data',
      isAdmin: true,
    },
    {
      id: '3',
      description: 'PROFILE.TABS.SUBSCRIPTIONS',
      route: 'subscriptions',
      onlyTypes: isAdmin
        ? [ERegistrationType.Customer, ERegistrationType.Visitor]
        : [ERegistrationType.Customer],
      requiredModules: [SystemModules.Assinaturas],
    },
    {
      id: '4',
      description: 'PROFILE.TABS.SERVICES',
      route: 'services',
      onlyTypes: [
        ERegistrationType.Customer,
        ERegistrationType.Visitor,
        ERegistrationType.User,
      ],
      requiredPlatformFeatures: [PLATFORM_FEATURES.workServices],
      requiredModules: [SystemModules.Servicos],
    },
    {
      id: '5',
      description: 'PROFILE.TABS.GOODS',
      route: 'sales',
      onlyTypes: [
        ERegistrationType.Customer,
        ERegistrationType.Visitor,
        ERegistrationType.User,
      ],
      requiredPlatformFeatures: [PLATFORM_FEATURES.goods],
      requiredModules: [SystemModules.Mercadorias],
    },
    {
      id: '6',
      description: 'PROFILE.TABS.FINANCIAL',
      route: 'financial',
      onlyTypes: [
        ERegistrationType.Customer,
        ERegistrationType.Visitor,
        ERegistrationType.User,
      ],
      requiredAnyModules: [
        SystemModules.Assinaturas,
        SystemModules.Mercadorias,
        SystemModules.Servicos,
      ],
    },
    {
      id: '7',
      description: 'TREINO.WORKOUT_SHEETS.PERSONAL_WORKOUT_SHEETS.WORKOUTS',
      route: 'workout-sheets',
      onlyTypes: [
        ERegistrationType.Customer,
        ERegistrationType.Visitor,
        ERegistrationType.User,
      ],
      requiredModules: [SystemModules.TreiNo],
      requiredPlatformFeatures: [PLATFORM_FEATURES.treino],
    },
    {
      id: '9',
      description: 'PROFILE.TABS.AFIG',
      route: 'afig',
      onlyTypes: [
        ERegistrationType.Customer,
        ERegistrationType.Visitor,
        ERegistrationType.User,
      ],
      requiredModules: [SystemModules.TreiNo],
      requiredPlatformFeatures: [PLATFORM_FEATURES.treino],
    },
    {
      id: '10',
      description: 'PROFILE.TABS.GRADUATIONS',
      route: 'graduations',
      onlyTypes: [
        ERegistrationType.Customer,
        ERegistrationType.Visitor,
        ERegistrationType.User,
      ],
      requiredModules: [SystemModules.TreiNo],
      requiredPlatformFeatures: [PLATFORM_FEATURES.treino],
    },
    {
      id: '11',
      description: 'PROFILE.TABS.CONTRACTS',
      route: 'contracts',
      onlyTypes: [
        ERegistrationType.Customer,
        ERegistrationType.Visitor,
        ERegistrationType.User,
      ],
      requiredAnyModules: [SystemModules.ActuarWeb, SystemModules.Contratos],
    },
    {
      id: '13',
      description: 'PROFILE.TABS.ACCESS',
      route: 'accesses',
      onlyTypes: [
        ERegistrationType.Customer,
        ERegistrationType.Visitor,
        ERegistrationType.User,
      ],
      requiredModules: [SystemModules.Acesso],
    },
    {
      id: '14',
      description: 'PROFILE.TABS.DOCUMENTS',
      route: 'documents',
      onlyTypes: [ERegistrationType.Customer],
    },
    {
      id: '15',
      description: 'PROFILE.TABS.COMMENTS',
      route: 'comments',
    },
    {
      id: '16',
      description: 'PROFILE.TABS.HELPDESK',
      route: 'helpdesk',
      onlyTypes: [ERegistrationType.Customer],
      isAdmin: true,
    },
    {
      id: '17',
      description: 'PROFILE.TABS.BILLS',
      route: 'bills',
      onlyTypes: [ERegistrationType.Customer],
      isAdmin: true,
    },
    {
      id: '18',
      description: 'PROFILE.TABS.RETURNS',
      route: 'returns',
      onlyTypes: [ERegistrationType.Customer],
      isPaguelaAdmin: true,
    },
    {
      id: '19',
      description: 'PROFILE.TABS.AVERAGE_TICKET',
      route: 'average-ticket',
      onlyTypes: [ERegistrationType.Customer],
      requiredAnyModules: [
        SystemModules.Assinaturas,
        SystemModules.Mercadorias,
        SystemModules.Servicos,
      ],
    },
    {
      id: '20',
      description: 'PROFILE.TABS.ACQUIRERS',
      route: 'acquirers',
      onlyTypes: [ERegistrationType.Customer],
      isPaguelaAdmin: true,
    },
    {
      id: '21',
      description: 'PROFILE.CREDIT_ASSESSMENT.CREDIT_ASSESSMENT',
      route: 'credit-assessment',
      onlyTypes: [ERegistrationType.Customer, ERegistrationType.Visitor],
      isAdmin: true,
    },
    {
      id: '22',
      description: 'PROFILE.TABS.MEETINGS',
      route: 'meetings',
      onlyTypes: [ERegistrationType.Customer],
      isAdmin: true,
    },
    {
      id: '23',
      description: 'PROFILE.TABS.CRM',
      route: 'crm',
      onlyTypes: [ERegistrationType.Customer, ERegistrationType.Visitor],
      requiredModules: [SystemModules.CRM],
    },
    {
      id: '24',
      description: 'PROFILE.TABS.ACCESS_POINTS',
      route: 'access-points',
      isAdmin: true,
      onlyTypes: [
        ERegistrationType.Customer,
        ERegistrationType.Visitor,
        ERegistrationType.User,
      ],
    },
    {
      id: '25',
      description: 'PROFILE.TABS.PAGUELA_TRANSACTIONS',
      route: 'paguela-transactions',
      onlyTypes: [ERegistrationType.Customer],
      hasPaguela: true,
    },
    {
      id: '26',
      description: 'PROFILE.TABS.MIGRATION',
      route: 'migration',
      isAdmin: true,
    },
  ];

  return mockTabs.filter((tab) => {
    const canShowByTypes =
      (tab.onlyTypes && tab.onlyTypes.includes(registrationType)) ||
      !tab.onlyTypes;
    const canShowByAdmin = (tab.isAdmin && isAdmin) || !tab.isAdmin;
    const canShowByPaguelaAdmin =
      (tab.isPaguelaAdmin && isPaguelaAdmin) || !tab.isPaguelaAdmin;
    const canShowByPaguela = (tab.hasPaguela && hasPaguela) || !tab.hasPaguela;
    const canShowByMobile = (tab.isMobile && isMobile) || !tab.isMobile;

    return (
      canShowByTypes &&
      canShowByAdmin &&
      canShowByPaguelaAdmin &&
      canShowByPaguela &&
      canShowByMobile
    );
  });
};

export const filterVisibleProfileTabs = (
  profileTabs: TProfileTabItem[],
  enabledPlatformFeatures: EnabledPlatformFeatures | null | undefined,
  isModuleAllowed: (moduleId: string) => boolean,
  isAnyModuleAllowed: (moduleIds: string[]) => boolean,
  hasPermission: (permissions: PermissionNumber[]) => boolean = () => true
): TProfileTabItem[] =>
  profileTabs.filter((tab) => {
    const hasRequiredPlatformFeatures = arePlatformFeaturesEnabled(
      enabledPlatformFeatures,
      tab.requiredPlatformFeatures
    );
    const hasRequiredModules =
      tab.requiredModules?.every((moduleId) => isModuleAllowed(moduleId)) ??
      true;

    const hasRequiredAnyModules =
      !tab.requiredAnyModules ||
      tab.requiredAnyModules.length === 0 ||
      isAnyModuleAllowed(tab.requiredAnyModules);

    const hasRequiredPermissions =
      !tab.requiredPermissions ||
      tab.requiredPermissions.length === 0 ||
      hasPermission(tab.requiredPermissions);

    return (
      hasRequiredPlatformFeatures &&
      hasRequiredModules &&
      hasRequiredAnyModules &&
      hasRequiredPermissions
    );
  });

export const applySavedTabOrder = <T extends { id: string }>(
  tabs: T[],
  savedOrder: string[] | null | undefined
): T[] => {
  if (!savedOrder?.length) {
    return tabs;
  }
  const ranks = new Map<string, number>();
  let inherited = -1;
  for (const tab of tabs) {
    const saved = savedOrder.indexOf(tab.id);
    if (saved !== -1) {
      inherited = saved;
    }
    ranks.set(tab.id, inherited);
  }
  return [...tabs].sort((a, b) => (ranks.get(a.id) ?? 0) - (ranks.get(b.id) ?? 0));
};

export const districts = [
  { Description: 'AC', Value: 'AC' },
  { Description: 'AL', Value: 'AL' },
  { Description: 'AM', Value: 'AM' },
  { Description: 'AP', Value: 'AP' },
  { Description: 'BA', Value: 'BA' },
  { Description: 'CE', Value: 'CE' },
  { Description: 'DF', Value: 'DF' },
  { Description: 'ES', Value: 'ES' },
  { Description: 'GO', Value: 'GO' },
  { Description: 'MA', Value: 'MA' },
  { Description: 'MG', Value: 'MG' },
  { Description: 'MS', Value: 'MS' },
  { Description: 'MT', Value: 'MT' },
  { Description: 'PA', Value: 'PA' },
  { Description: 'PB', Value: 'PB' },
  { Description: 'PE', Value: 'PE' },
  { Description: 'PI', Value: 'PI' },
  { Description: 'PR', Value: 'PR' },
  { Description: 'RR', Value: 'RR' },
  { Description: 'RO', Value: 'RO' },
  { Description: 'RS', Value: 'RS' },
  { Description: 'SC', Value: 'SC' },
  { Description: 'SE', Value: 'SE' },
  { Description: 'SP', Value: 'SP' },
  { Description: 'TO', Value: 'TO' },
  { Description: 'RJ', Value: 'RJ' },
  { Description: 'RN', Value: 'RN' },
];

export const commentActions = [
  {
    id: 'view',
    description: 'PROFILE.COMMENTS.VIEW',
  },
  {
    id: 'edit',
    description: 'PROFILE.COMMENTS.EDIT',
  },
  {
    id: 'read',
    description: 'PROFILE.COMMENTS.MARK_AS_READ',
  },
  {
    id: 'delete',
    description: 'PROFILE.COMMENTS.DELETE',
  },
];

export enum EKinshipType {
  BoyfriendGirlfriend = 0,
  Spouse = 1,
  Sibling = 2,
  Parent = 3,
  UncleAunt = 4,
  Grandparent = 5,
  Guardian = 6,
  Others = 7,
}

export enum ETaxRegimeType {
  Nenhum = 0,
  SimplesNacional = 1,
  SimplesNacionalExcesso = 2,
  NormalLucroPresumido = 3,
  NormalLucroReal = 4,
}

export enum ESpecialTaxRegimeType {
  SemRegimeTributarioEspecial = 0,
  MicroEmpresaMunicipal = 1,
  Estimativa = 2,
  SociedadeDeProfissionais = 3,
  Cooperativa = 4,
  MicroempresarioIndividualMEI = 5,
  MicroempresaOuPequenoPorteMEEPP = 6,
  LucroReal = 7,
  LucroPresumido = 8,
  TributacaoNormal = 9,
  SimplesNacionalComExcessoDoSublimite = 10,
  EmpresaDeResponsabilidadeLimitada = 11,
}
export interface IPersonWithIncludesParams {
  id?: string | null;
  personId: string;
  includes?: number[] | null;
}

export enum EPersonIncludeType {
  Access,
  Addresses,
  TargetComments,
  Integration,
  Phones,
  Preference,
  Responsible,
  Tax,
  EventConsolidated,
  FinancialConsolidated,
  HealthConsolidated,
  HistoryConsolidated,
  PhoneConsolidated,
  SubscriptionConsolidated,
  TechInfoConsolidated,
  UserConsolidated,
  StorageConsolidated,
  Ties,
}

export enum ETimelineEvent {
  SaleCreated,
  SaleDeleted,
  CustomerCreated,
  CustomerCreatedHotSite,
  VisitorCreated,
  VisitorConverted,
  VisitorAutoConverted,
  CustomerInactivated,
  CustomerAutoInactivated,
  CommentCreated,
  CommentContentEdited,
  CommentTitleEdited,
  CommentEdited,
  CommentDeleted,
  SubscriptionCreated,
  SubscriptionRenewed,
  SubscriptionAutoRenewed,
  SubscriptionBreak,
  SubscriptionDeleted,
  SubscriptionEnded,
  AccountsReceivableReceived,
  ReceiptsReversed,
  WorkoutPlanCreated,
  PhysicalAssessmentCreated,
  ServiceCreated,
  ServiceDeleted,
  ActivityConsumed,
  GoodConsumed,
  ServiceConsumed,
  ModuleConsumed,
  ActivityReturned,
  GoodReturned,
  ServiceReturned,
  ModuleReturned,
  CustomerNegativated,
  CustomerNegativationNoteChanged,
  CustomerNegativationRemoved,
  // Feature 002/003/004: appended by the backend, same ordinals. The auditable
  // ones are mirrored by `EAuditEvent` (`@settings/data-access`); a spec keeps
  // the two in step.
  CustomerActivated = 37,
  InactivationBlocked = 38,
  InactivationChoice = 39,
  SubscriptionUnpaused = 40,
  SubscriptionFinished = 41,
  SubscriptionSwitched = 42,
  SubscriptionTransferred = 43,
  RenewalsDeleted = 44,
  AutoRenewalCanceled = 45,
  SentToCrm = 46,
  /** Only in Ajustes › Auditoria (rule saves); never on a person's timeline. */
  InactivationRulesSaved = 47,
  AccessManuallyReleased = 48,
}

/**
 * The `Details` shape of each timeline event that has one (003-data-model,
 * "details por evento"; 004-plan, `details.snapshot` and `SentToCrm`). The
 * shapes live in `@settings/data-access` (`audit-trail.model.ts`), shared
 * with Ajustes › Auditoria.
 */
export type TTimelineDetailsByEvent = {
  [ETimelineEvent.CustomerInactivated]: TCustomerStatusChangedDetails;
  [ETimelineEvent.CustomerAutoInactivated]: TCustomerAutoInactivatedDetails;
  [ETimelineEvent.SubscriptionBreak]: TSubscriptionBreakDetails;
  [ETimelineEvent.SubscriptionDeleted]: TSubscriptionDeletedDetails;
  [ETimelineEvent.SubscriptionEnded]: TSubscriptionEndedDetails;
  [ETimelineEvent.CustomerActivated]: TCustomerStatusChangedDetails;
  [ETimelineEvent.InactivationBlocked]: TInactivationBlockedDetails;
  [ETimelineEvent.InactivationChoice]: TInactivationChoiceDetails;
  [ETimelineEvent.SubscriptionUnpaused]: TSubscriptionUnpausedDetails;
  [ETimelineEvent.SubscriptionFinished]: TSubscriptionFinishedDetails;
  [ETimelineEvent.SubscriptionSwitched]: TSubscriptionMovedDetails;
  [ETimelineEvent.SubscriptionTransferred]: TSubscriptionMovedDetails;
  [ETimelineEvent.RenewalsDeleted]: TRenewalsDeletedDetails;
  [ETimelineEvent.AutoRenewalCanceled]: TAutoRenewalCanceledDetails;
  [ETimelineEvent.SentToCrm]: TSentToCrmDetails;
  [ETimelineEvent.InactivationRulesSaved]: TInactivationRulesSavedDetails;
};

/** `Details` of event `E`; `never` (so `null`) for events that carry none. */
export type TTimelineDetailsOf<E extends ETimelineEvent> =
  E extends keyof TTimelineDetailsByEvent ? TTimelineDetailsByEvent[E] : never;

/** `TimelineRowViewModel` without the event-specific part. */
export type TTimelineEventBase = {
  Category: string;
  Title: string;
  Description: string;
  Extra: string;
  EventId: string;
  EventBy: string;
  EventByName: string;
  /** Brasília wall time, whatever the account's zone: sorts the rows, never shown. */
  EventOn: string;
  /**
   * The same instant in UTC, which the `DateInterceptor` hands over in the
   * account's wall time: this is the one shown. `null` only on rows without any
   * time, where `EventOn` is missing too.
   */
  EventOnUtc: string | null;
  PartsTitle?: (string | number)[];
  PartsDescription?: (string | number)[];
  // Audit trail (feature 003 F5). Empty on old rows and on events without it.
  // Optional because a backend without F5 does not send them.
  Origin?: EAuditOrigin | null;
  SubscriptionId?: string | null;
  /** Effective date (close, pause start or end…). Instant. */
  EffectiveOnUtc?: string | null;
  /** Reason code (`CloseReason`, `PersonHasBindingSubscription`…). */
  Reason?: string | null;
  InactivationRuleVersionId?: string | null;
  /** The rule version in force on an automatic inactivation. */
  InactivationRuleVersion?: number | null;
  /** "Sistema · regra salva por …": who answers for a system action. */
  ResponsibleBy?: string | null;
  ResponsibleByName?: string | null;
  IpAddress?: string | null;
  UserAgent?: string | null;
  /** The row opens its details: status changes of the file or the plan, with details. */
  Expandable?: boolean;
};

/**
 * One timeline row, discriminated by `CategoryId`: narrowing the event narrows
 * `Details` — `if (row.CategoryId === ETimelineEvent.CustomerAutoInactivated)
 * row.Details?.conditions`. `Details` comes as an object (the jsonb as is);
 * its `*Utc` keys and `neverSince` are converted to the account's wall time
 * like the row fields.
 */
export type TTimelineEvent = {
  [E in ETimelineEvent]: TTimelineEventBase & {
    CategoryId: E;
    Details?: TTimelineDetailsOf<E> | null;
  };
}[ETimelineEvent];

export const timelineIcons: Partial<Record<ETimelineEvent, string>> = {
  [ETimelineEvent.SaleCreated]: 'box-open',
  [ETimelineEvent.SaleDeleted]: 'cross-circle',
  [ETimelineEvent.CustomerCreated]: 'review',
  [ETimelineEvent.CustomerCreatedHotSite]: 'review',
  [ETimelineEvent.VisitorCreated]: 'user',
  [ETimelineEvent.VisitorConverted]: 'review',
  [ETimelineEvent.VisitorAutoConverted]: 'review',
  [ETimelineEvent.CustomerInactivated]: 'cross-circle',
  [ETimelineEvent.CustomerAutoInactivated]: 'cross-circle',
  [ETimelineEvent.CommentCreated]: 'note-sticky',
  [ETimelineEvent.CommentContentEdited]: 'pen-square',
  [ETimelineEvent.CommentTitleEdited]: 'pen-square',
  [ETimelineEvent.CommentEdited]: 'pen-square',
  [ETimelineEvent.CommentDeleted]: 'cross-circle',
  [ETimelineEvent.SubscriptionCreated]: 'memo-circle-check',
  [ETimelineEvent.SubscriptionRenewed]: 'memo-circle-check',
  [ETimelineEvent.SubscriptionAutoRenewed]: 'memo-circle-check',
  [ETimelineEvent.SubscriptionBreak]: 'cross-circle',
  [ETimelineEvent.SubscriptionDeleted]: 'cross-circle',
  [ETimelineEvent.SubscriptionEnded]: 'cross-circle',
  [ETimelineEvent.AccountsReceivableReceived]: 'bank',
  [ETimelineEvent.ReceiptsReversed]: 'refund-alt',
  [ETimelineEvent.ServiceCreated]: 'heart-partner-handshake',
  [ETimelineEvent.ServiceDeleted]: 'cross-circle',
  [ETimelineEvent.ActivityConsumed]: 'bolt',
  [ETimelineEvent.GoodConsumed]: 'bolt',
  [ETimelineEvent.ServiceConsumed]: 'bolt',
  [ETimelineEvent.ModuleConsumed]: 'bolt',
  [ETimelineEvent.ActivityReturned]: 'cross-circle',
  [ETimelineEvent.GoodReturned]: 'cross-circle',
  [ETimelineEvent.ServiceReturned]: 'cross-circle',
  [ETimelineEvent.ModuleReturned]: 'cross-circle',
  [ETimelineEvent.CustomerNegativated]: 'triangle-warning',
  [ETimelineEvent.CustomerNegativationNoteChanged]: 'pen-square',
  [ETimelineEvent.CustomerNegativationRemoved]: 'cross-circle',
  [ETimelineEvent.AccessManuallyReleased]: 'unlock',
};

export const timelineSvgs: Partial<Record<ETimelineEvent, string>> = {
  [ETimelineEvent.WorkoutPlanCreated]: 'treino-letter',
  [ETimelineEvent.PhysicalAssessmentCreated]: 'afig-small',
};

export const templateByParts: Record<ETimelineEvent, string[]> = {
  [ETimelineEvent.SaleCreated]: ['link', 'date', 'addedBy'],
  [ETimelineEvent.SaleDeleted]: ['date', 'addedBy'],
  [ETimelineEvent.CustomerCreated]: ['date', 'addedBy'],
  [ETimelineEvent.CustomerCreatedHotSite]: ['date', 'addedBy'],
  [ETimelineEvent.VisitorCreated]: ['date', 'addedBy'],
  [ETimelineEvent.VisitorConverted]: ['date', 'addedBy'],
  [ETimelineEvent.VisitorAutoConverted]: ['date', 'addedBy'],
  [ETimelineEvent.CustomerInactivated]: ['date', 'addedBy'],
  [ETimelineEvent.CustomerAutoInactivated]: ['date', 'addedBy'],
  [ETimelineEvent.CommentCreated]: ['link', 'date', 'addedBy'],
  [ETimelineEvent.CommentContentEdited]: ['link', 'date', 'addedBy'],
  [ETimelineEvent.CommentTitleEdited]: ['link', 'date', 'addedBy'],
  [ETimelineEvent.CommentEdited]: ['link', 'date', 'addedBy'],
  [ETimelineEvent.CommentDeleted]: ['date', 'addedBy'],
  [ETimelineEvent.SubscriptionCreated]: ['date', 'addedBy'],
  [ETimelineEvent.SubscriptionRenewed]: ['date', 'addedBy'],
  [ETimelineEvent.SubscriptionAutoRenewed]: ['date', 'addedBy'],
  [ETimelineEvent.SubscriptionBreak]: ['date', 'addedBy'],
  [ETimelineEvent.SubscriptionDeleted]: ['date', 'addedBy'],
  [ETimelineEvent.SubscriptionEnded]: ['date', 'addedBy'],
  [ETimelineEvent.AccountsReceivableReceived]: ['date', 'addedBy'],
  [ETimelineEvent.ReceiptsReversed]: ['date', 'addedBy'],
  [ETimelineEvent.WorkoutPlanCreated]: ['link', 'date', 'addedBy'],
  [ETimelineEvent.PhysicalAssessmentCreated]: ['link', 'date', 'addedBy'],
  [ETimelineEvent.ServiceCreated]: ['link', 'date', 'addedBy'],
  [ETimelineEvent.ServiceDeleted]: ['date', 'addedBy'],
  [ETimelineEvent.ActivityConsumed]: ['date', '', 'addedBy'],
  [ETimelineEvent.GoodConsumed]: ['date', '', 'addedBy'],
  [ETimelineEvent.ServiceConsumed]: ['date', '', 'addedBy'],
  [ETimelineEvent.ModuleConsumed]: ['date', '', 'addedBy'],
  [ETimelineEvent.ActivityReturned]: ['date', '', 'addedBy'],
  [ETimelineEvent.GoodReturned]: ['date', '', 'addedBy'],
  [ETimelineEvent.ServiceReturned]: ['date', '', 'addedBy'],
  [ETimelineEvent.ModuleReturned]: ['date', '', 'addedBy'],
  [ETimelineEvent.CustomerNegativated]: ['date', 'addedBy'],
  [ETimelineEvent.CustomerNegativationNoteChanged]: ['date', 'addedBy'],
  [ETimelineEvent.CustomerNegativationRemoved]: ['date', 'addedBy'],
  [ETimelineEvent.CustomerActivated]: ['date', 'addedBy'],
  [ETimelineEvent.InactivationBlocked]: ['date', 'addedBy'],
  [ETimelineEvent.InactivationChoice]: ['date', 'addedBy'],
  [ETimelineEvent.SubscriptionUnpaused]: ['date', 'addedBy'],
  [ETimelineEvent.SubscriptionFinished]: ['date', 'addedBy'],
  [ETimelineEvent.SubscriptionSwitched]: ['date', 'addedBy'],
  [ETimelineEvent.SubscriptionTransferred]: ['date', 'addedBy'],
  [ETimelineEvent.RenewalsDeleted]: ['date', 'addedBy'],
  [ETimelineEvent.AutoRenewalCanceled]: ['date', 'addedBy'],
  [ETimelineEvent.SentToCrm]: ['date', 'addedBy'],
  [ETimelineEvent.InactivationRulesSaved]: ['date', 'addedBy'],
  [ETimelineEvent.AccessManuallyReleased]: ['date', 'addedBy'],
};

export type TGraduation = {
  PersonActivityLevelId: string;
  ActivityId: string;
  PersonId: string;
  TeacherId: string;
  SubLevelId: string;
  GraduationOn: string;
  LevelId: string;
  PersonName?: string;
};

export type TGraduationList = {
  PersonActivityLevelId: string;
  PersonId: string;
  ActivityId: string;
  ActivityName: string;
  TeacherId: string;
  TeacherName: string;
  LevelName: string;
  Color: string;
  SecondaryColor?: string | null;
  SubLevelId: string;
  SublevelName: string;
  SubLevelOrder: number;
  GraduationOn: string;
  Level: {
    LevelId: string;
    Name: string;
    Color: string;
    SecondaryColor?: string | null;
    ActivityId: string;
    SubLevelCount: number;
    SubLevels: {
      SubLevelId: string;
      Name: string;
      SubLevelOrder: number;
      LevelId: string;
    }[];
  };
};

export type TPersonIntegration = {
  GympassId: string;
  PersonId: string;
  TenantId: string;
  TotalpassId: string;
};

export type TPaguelaAccount = {
  ContaId: string;
  Nome: string;
  Sobrenome: string;
  TipoConta: string;
  Senha: string;
  TipoCadastro: string;
  Email: string;
  Telefone: string;
  CpfCnpj: string;
  RetornoUrl: string;
  RazaoSocial: string;
  Fantasia: string;
  InscricaoMunicipal: string;
  VendedorId: string;
  LogoUrl: string;
  Situacao: EPaguelaSituation;
  Cedentes: any[];
  Enderecos: {
    EnderecoId: string;
    ContaId: string;
    Logradouro: string;
    Numero: string;
    Complemento: string;
    Bairro: string;
    Cidade: string;
    Estado: string;
    Cep: string;
  }[];
  Cartoes: any[];
  Objetos: any[];
  Adquirentes: {
    AdquirenteId: string;
    Nome: string;
    ContaId: string;
    GatewayId: string;
    GatewayKey: string;
    UrlRequisicao: string;
    UrlConsulta: string;
    TipoMeioPagamento: string;
    MeioPagamento: string;
    Padrao: boolean;
    DataExclusao: string;
    DataCadastro: string;
    DataModificacao: string;
    Conta: any;
  }[];
  InformacoesBancarias: {
    InformacaoBancariaId: string;
    TipoConta: string;
    Agencia: string;
    AgenciaDigito: string;
    Banco: string;
    NumeroConta: string;
    ContaDigito: string;
    ContaBancariaZoop: any;
  }[];
  Pendencias: any[];
};

export type TSchoolEnrollment = {
  EnrollmentId: string;
  PersonId: string;
  PersonName: string | null;
  SchoolClassId: string;
  SchoolClassName: string | null;
  GradeLevelId: string;
  GradeLevelName: string | null;
  AcademicYearId: string;
  EnrollmentStatus: number;
  StudentRegistrationNumber: string | null;
  EnrolledOnUtc: string;
  CancelledOnUtc?: string | null;
};
